import type { PropsWithChildren } from 'react';

import { Center, Loader } from '@mantine/core';
import { Navigate } from 'react-router';

import { useAssistantStatus } from '@/hooks/useAssistantStatus';

/**
 * This gate checks the `/assistant` route with the same per-user signal that the sidebar entry
 * points use (`useAssistantStatus`). Without this check, a user excluded by the feature flag
 * could still reach the page through a bookmark, a pasted link, or a typed URL. That user would
 * see a chat that looks ready, then get a 503 error only after they send a message.
 *
 * There are three states, in order:
 * - **pending** — Show a loader that matches `SuspenseLoader`'s fallback. This stops any flash
 *   while the status request is in flight. `AssistantProvider` shares the same query key, so
 *   this state never renders when the cache is already warm.
 * - **disabled** — Redirect to the dashboard. This matches how `SecureRoute` handles a route the
 *   user cannot access. A failed status request also lands here. This is correct: if the
 *   endpoint is down, the feature stays hidden instead of half-open.
 * - **enabled** — Render the page.
 */
export function AssistantRouteGate({ children }: PropsWithChildren) {
	const { data, isPending } = useAssistantStatus();

	if (isPending) {
		return (
			<Center mih="300px">
				<Loader />
			</Center>
		);
	}

	if (!data?.enabled) {
		return <Navigate to="/dashboard" replace />;
	}

	return children;
}
